import React, { useState } from 'react'
import RichText from '../RichText'
import Image from '../Image'
import { IContentModule } from './types'

// MUI
import { alpha, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import ReactPlayer from 'react-player'
import { IconButton, SvgIcon } from '@mui/material'
import PlayIcon from '@tdcerhverv/parrotfish/dist/icons/Icons/Play.svg'
import { BLOCKS, ContentfulToComponent, ContentfulToComponentProps, IRichText, RenderNode } from '../RichText/types'
import ParrotfishIcon from '../ParrotfishIcon'
import List, { IconBullet } from '../List'
import CallToAction from '../CallToAction'
import Dialog from '../Dialog'
import LinkConverter from '../LinkConverter'
import Form from '../Form'
import Faq from '../Faq'
import CodeModule from '../CodeModule'
import Feedback from '../Feedback'

const ContentModule = ({
  title,
  subtitle,
  image,
  video,
  richText,
  renderNode,
  contentAlignment,
  sx,
}: IContentModule) => {
  const defaultRenderNode: Partial<RenderNode> = {
    [BLOCKS.HEADING_2]: (node, children) => {
      const element = (
        <Typography component="h2" variant={'h3'} sx={styles.h2}>
          {children}
        </Typography>
      )
      return element
    },
  }
  const { palette } = useTheme()
  const styles = {
    root: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    h2: {
      scrollMarginTop: '80px',
      marginBottom: '.5em',
      wordBreak: 'break-word',
      '&:only-child': {
        marginTop: '-5px',
      },
      '&:first-child': {
        marginTop: 0,
      },
    },
    title: {
      marginBottom: '16px',
      wordBreak: 'break-word',
    },
    subtitle: {
      marginBottom: '24px',
      wordBreak: 'break-word',
    },
    player: {
      position: 'relative',
      marginBottom: '1em',
    },
    image: {
      height: '100%',
      width: '100%',
    },
    playerPlayIcon: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      zIndex: 50,
      backgroundColor: alpha(palette.common.white, 0.8),
      borderRadius: '50%',
      height: '100px',
      width: '100px',
    },
    richText: {
      flex: 1 as const,
      display: 'flex',
      flexDirection: 'column' as const,
      '& a': {
        alignSelf: 'baseline',
        marginTop: 'auto',
      },
      '& button': {
        alignSelf: 'baseline',
        marginTop: 'auto',
      },
    },
  }
  const [videoState, setVideoState] = useState({ playing: false })
  const safeTitle = title ? title.trim() : title
  const safeSubTitle = subtitle ? subtitle.trim() : subtitle

  const handlePlay = () => {
    setVideoState({ playing: true })
  }

  const propsMap: Partial<ContentfulToComponentProps> = {
    ContentfulComponentIcon: <IParrotfishIcon,>(props: IParrotfishIcon) => ({
      ...(props as unknown as IParrotfishIcon),
    }),

    ContentfulComponentList: <IList,>(props: IList) => ({
      ...(props as unknown as IList),
    }),
    ContentfulComponentCta: <CallToActionType,>(props: CallToActionType) => ({
      ...(props as unknown as CallToActionType),
    }),
    ContentfulComponentDialog: <DialogProps,>(props: DialogProps) => ({
      ...(props as unknown as DialogProps),
    }),
    ContentfulComponentLink: <ILinkConverter,>(props: ILinkConverter) => ({
      ...(props as unknown as ILinkConverter),
    }),
    ContentfulComponentFaq: <IFaq,>(props: IFaq) => ({
      ...(props as unknown as IFaq),
    }),
    ContentfulModuleForm: <FormProps,>(props: FormProps) => ({
      ...(props as unknown as FormProps),
    }),
    ContentfulComponentIconBullet: <IIconBullet,>(props: IIconBullet) => ({
      ...(props as unknown as IIconBullet),
    }),
    ContentfulModuleDynamicComponents: <CodeModuleComponentProps,>(props: CodeModuleComponentProps) => ({
      ...(props as unknown as CodeModuleComponentProps),
    }),
    ContentfulComponentFeedback: <IFeedback,>(props: IFeedback) => ({
      ...(props as unknown as IFeedback),
    }),
  } as const

  const contentComponentMap: Partial<ContentfulToComponent> = {
    'ContentfulComponentIcon': ParrotfishIcon,
    'ContentfulComponentList': List,
    'ContentfulComponentCta': CallToAction,
    'ContentfulComponentDialog': Dialog,
    'ContentfulComponentLink': LinkConverter,
    'ContentfulComponentFaq': Faq,
    'ContentfulModuleForm': Form,
    'ContentfulComponentIconBullet': IconBullet,
    'ContentfulModuleDynamicComponents': CodeModule,
    'ContentfulComponentFeedback': Feedback,
  } as const

  return (
    <Box sx={{ ...styles.root, textAlign: contentAlignment || 'left' }}>
      {safeTitle && (
        <Typography sx={[styles.title, sx && sx.title ? sx.title : {}]} component="h2" variant="h6">
          {safeTitle}
        </Typography>
      )}
      {safeSubTitle && (
        <Typography
          sx={[styles.subtitle, sx && sx.subtitle ? sx.subtitle : {}]}
          component={title ? 'p' : 'h2'}
          variant="h4"
        >
          {safeSubTitle}
        </Typography>
      )}
      {!!video && !!image && (
        <Box sx={styles.player}>
          {!videoState.playing ? (
            <>
              <IconButton onClick={handlePlay} sx={styles.playerPlayIcon}>
                <SvgIcon component={PlayIcon} viewBox="0 0 48 48" />
              </IconButton>
              <Image {...image} imageStyles={styles?.image || ''} />
            </>
          ) : (
            <ReactPlayer
              url={video.file?.url || ''}
              onPlay={handlePlay}
              playing={videoState.playing}
              height={'100%'}
              width={'100%'}
              controls={true}
            ></ReactPlayer>
          )}
        </Box>
      )}
      {video === null && !!image && <Image {...image} imageStyles={sx?.image || {}} />}
      {richText && (
        <RichText
          sx={{
            ...styles.richText,
            ...(sx && sx.richText ? sx.richText : {}),
          }}
          {...(richText as IRichText)}
          renderNode={renderNode || defaultRenderNode}
          parentModule={'content-module'}
          componentMap={contentComponentMap as ContentfulToComponent}
          propsMap={propsMap as ContentfulToComponentProps}
        />
      )}
    </Box>
  )
}

export default ContentModule
